import React, { useEffect, useState } from 'react';
import { axiosClient, useInterceptors } from '../../api/axiosClient';
import { appConfig } from '../../config/appConfig';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { toNumber } from '../../helpers/toNumber';
import { parseJwt } from 'helpers/jwt';
import { Api } from 'api';

type Props = {
  children: JSX.Element | JSX.Element[];
  onAuthenticated: (fullName: string) => void;
};

export const Auth: React.FC<Props> = ({ children, onAuthenticated }: Props) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { pathname, search } = useLocation();
  const history = useHistory();
  let timeoutId: any = null;
  const refreshIntervalInMinutes = 29;
  useInterceptors(history);

  useEffect(() => {
    if (isAuthenticated) return;

    const queryParams = queryString.parse(search);

    const dataEnvironmentId = toNumber(queryParams.dataEnvironmentId);
    let jwt = queryParams.jwt as string;
    let returnUrl;

    const requestedUrl = queryString.stringifyUrl({
      url: pathname,
      query: {
        ...queryParams,
        dataEnvironmentId: undefined,
        jwt: undefined,
      },
    });

    if (dataEnvironmentId) {
      localStorage.setItem('dataEnvironmentId', dataEnvironmentId.toString());
    }

    if (jwt) {
      sessionStorage.setItem('jwt', jwt);
      returnUrl = sessionStorage.getItem('returnUrl');
      sessionStorage.removeItem('returnUrl');
    } else {
      jwt = sessionStorage.getItem('jwt') as string;

      if (!jwt) {
        sessionStorage.removeItem('jwt');
        sessionStorage.setItem('returnUrl', requestedUrl);
        window.location.href = appConfig.loginUrl;
        return;
      }
    }

    const decoded = parseJwt(jwt);
    const normalized = normalizeClaims(decoded);

    onAuthenticated(normalized?.fullName);

    // @ts-ignore
    axiosClient.defaults.headers['Authorization'] = `Bearer ${jwt}`;

    setIsAuthenticated(true);
    // TODO: add user state. 'true' after initial auth request, 'false' - for any 403 request (remains false till user tries to login)
    window.sessionStorage.setItem('hasAccess', 'true');

    if (jwt) {
      // refresh token now so we have a fresh one
      refreshToken();
    }

    const routeState = { ...queryParams, jwt };
    history.replace(returnUrl ? returnUrl : requestedUrl, routeState);

    function normalizeClaims(decoded: any) {
      if (!decoded) {
        return null;
      }

      const CLAIM_BASE = "https://gateway.solovis.com/identity/claims";

      return {
        fullName: decoded[`${CLAIM_BASE}/fullname`] ?? null,
        email: decoded[`${CLAIM_BASE}/primaryemail`] ?? null,
        userId: decoded[`${CLAIM_BASE}/userid`] ?? null,
        raw: decoded
      };
    }

    // refresh token every 29 minutes
    function refreshToken() {
      clearTimeout(timeoutId);
      Api.auth
        .RefreshToken()
        .then((resp) => {
          const jwtToken = resp.data.Jwt;
          sessionStorage.setItem('jwt', jwtToken);
          // @ts-ignore
          axiosClient.defaults.headers['Authorization'] = `Bearer ${jwtToken}`;
          timeoutId = setTimeout(
            () => refreshToken(),
            refreshIntervalInMinutes * 60 * 1000
          );
        })
        .catch((err) => console.log(err));
    }

    return () => clearTimeout(timeoutId);
  }, [history, isAuthenticated, pathname, search, onAuthenticated]);

  return isAuthenticated ? <>{children}</> : null;
};

export default Auth;
